import React, { useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_TOTAL_FILE_SIZE = 10 * 1024 * 1024;

const allowedExtensions = ["pdf", "jpg", "jpeg", "png"];

const profileLabels = {
  particulier: "Particulier",
  entreprise: "Entreprise",
  etudiant: "Étudiant",
  diaspora: "Diaspora",
};

const financingLabels = {
  "pret-personnel": "Prêt personnel",
  "avance-salaire": "Avance sur salaire",
  equipement: "Achat d’équipement",
  sante: "Santé",
  logement: "Logement",
  mariage: "Mariage",
  "projet-familial": "Projet familial",
  autre: "Autre besoin",
  tresorerie: "Avance de trésorerie",
  salaires: "Paiement des salaires",
  stock: "Achat de stock",
  materiel: "Achat de matériel",
  fournisseurs: "Paiement des fournisseurs",
  developpement: "Développement",
  investissement: "Investissement",
  "frais-universitaires": "Frais universitaires",
  ordinateur: "Achat d’un ordinateur",
  "logement-etudiant": "Logement étudiant",
  "voyage-etudes": "Voyage d’études",
  formation: "Formation",
  "investissement-guinee": "Investissement en Guinée",
  "aide-familiale": "Aide familiale",
  immobilier: "Immobilier",
  commerce: "Commerce",
  agriculture: "Agriculture",
  entrepreneuriat: "Entrepreneuriat",
};

const documentsByProfile = {
  particulier: [
    {
      id: "identity",
      icon: "🪪",
      title: "Pièce d’identité",
      description:
        "Carte nationale d’identité, passeport ou document équivalent.",
      required: true,
    },
    {
      id: "address",
      icon: "🏠",
      title: "Justificatif de résidence",
      description:
        "Facture, attestation de résidence ou justificatif de domicile.",
      required: false,
    },
    {
      id: "income",
      icon: "💼",
      title: "Justificatif de revenus",
      description:
        "Bulletin de salaire, attestation de travail ou preuve d’activité.",
      required: true,
    },
    {
      id: "bankStatement",
      icon: "🏦",
      title: "Relevé bancaire",
      description:
        "Relevé bancaire récent permettant d’étudier votre situation.",
      required: false,
    },
  ],

  entreprise: [
    {
      id: "rccm",
      icon: "🏢",
      title: "Document RCCM",
      description:
        "Copie lisible du registre du commerce de l’entreprise.",
      required: true,
    },
    {
      id: "nif",
      icon: "📋",
      title: "Document NIF",
      description:
        "Justificatif du numéro d’identification fiscale.",
      required: true,
    },
    {
      id: "bankStatement",
      icon: "🏦",
      title: "Relevé bancaire professionnel",
      description:
        "Relevé bancaire récent du compte de l’entreprise.",
      required: true,
    },
    {
      id: "activityProof",
      icon: "📊",
      title: "Justificatif d’activité",
      description:
        "Factures, contrats, états financiers ou preuve d’activité récente.",
      required: true,
    },
  ],

  etudiant: [
    {
      id: "identity",
      icon: "🪪",
      title: "Pièce d’identité",
      description:
        "Carte nationale d’identité, passeport ou document équivalent.",
      required: true,
    },
    {
      id: "enrollment",
      icon: "🎓",
      title: "Certificat d’inscription",
      description:
        "Certificat de scolarité ou attestation d’inscription en cours.",
      required: true,
    },
    {
      id: "educationCost",
      icon: "🧾",
      title: "Justificatif des frais",
      description:
        "Facture, devis ou document indiquant le montant à financer.",
      required: true,
    },
    {
      id: "guarantor",
      icon: "🤝",
      title: "Justificatif du garant",
      description:
        "Pièce d’identité et justificatif de revenus du garant.",
      required: true,
    },
  ],

  diaspora: [
    {
      id: "identity",
      icon: "🛂",
      title: "Passeport ou pièce d’identité",
      description:
        "Copie lisible de votre passeport ou pièce d’identité.",
      required: true,
    },
    {
      id: "residence",
      icon: "🌍",
      title: "Justificatif de résidence",
      description:
        "Titre de séjour, visa ou justificatif de résidence à l’étranger.",
      required: true,
    },
    {
      id: "income",
      icon: "💼",
      title: "Justificatif de revenus",
      description:
        "Bulletin de salaire, contrat de travail ou preuve d’activité.",
      required: true,
    },
    {
      id: "bankStatement",
      icon: "🏦",
      title: "Relevé bancaire",
      description:
        "Relevé bancaire récent dans votre pays de résidence.",
      required: true,
    },
    {
      id: "projectProof",
      icon: "📁",
      title: "Justificatif du projet",
      description:
        "Devis, facture, titre foncier ou document lié au projet en Guinée.",
      required: false,
    },
  ],
};

function FinanceDocuments() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [errors, setErrors] = useState({});

  const profile = searchParams.get("profil");
  const financingType = searchParams.get("type");

  const formData = location.state?.formData || {};

  const profileLabel =
    profileLabels[profile] || "Non renseigné";

  const financingLabel =
    financingLabels[financingType] || "Non renseigné";

  const requiredDocuments = useMemo(() => {
    const baseDocuments = documentsByProfile[profile] || [];

    if (
      profile === "entreprise" &&
      financingType === "salaires"
    ) {
      return [
        ...baseDocuments,
        {
          id: "payroll",
          icon: "👥",
          title: "État des salaires",
          description:
            "Liste des salariés concernés et montant des salaires à financer.",
          required: true,
        },
      ];
    }

    return baseDocuments;
  }, [profile, financingType]);

  const requiredCount = requiredDocuments.filter(
    (document) => document.required
  ).length;

  const uploadedRequiredCount = requiredDocuments.filter(
    (document) =>
      document.required && uploadedDocuments[document.id]
  ).length;

  const allRequiredDocumentsUploaded =
    requiredCount > 0 &&
    uploadedRequiredCount === requiredCount;

  const handleContinue = () => {
    if (!allRequiredDocumentsUploaded) {
      return;
    }

    navigate(
      `/consulting/finance/verification?profil=${profile}&type=${financingType}`,
      {
        state: {
          formData,
          uploadedDocuments,
        },
      }
    );
  };

  const handleFileSelection = (documentId, event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const extension = file.name
      .split(".")
      .pop()
      ?.toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        [documentId]:
          "Format non autorisé. Utilisez un fichier PDF, JPG, JPEG ou PNG.",
      }));

      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        [documentId]:
          "Le fichier dépasse la taille maximale autorisée de 5 Mo.",
      }));

      event.target.value = "";
      return;
    }

    const currentTotalSize = Object.entries(uploadedDocuments)
      .filter(
        ([currentDocumentId]) =>
          currentDocumentId !== documentId
      )
      .reduce(
        (totalSize, [, currentFile]) =>
          totalSize + (currentFile?.size || 0),
        0
      );

    const newTotalSize = currentTotalSize + file.size;

    if (newTotalSize > MAX_TOTAL_FILE_SIZE) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        [documentId]:
          "La taille totale des justificatifs ne doit pas dépasser 10 Mo.",
      }));

      event.target.value = "";
      return;
    }

    setUploadedDocuments((currentDocuments) => ({
      ...currentDocuments,
      [documentId]: file,
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      [documentId]: "",
    }));

    event.target.value = "";
  };

  const removeDocument = (documentId) => {
    setUploadedDocuments((currentDocuments) => {
      const updatedDocuments = { ...currentDocuments };
      delete updatedDocuments[documentId];
      return updatedDocuments;
    });

    setErrors((currentErrors) => ({
      ...currentErrors,
      [documentId]: "",
    }));
  };

  const formatFileSize = (size) => {
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} Ko`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
  };

  return (
    <div style={styles.page}>
      <button
        type="button"
        style={styles.backButton}
        onClick={() => navigate(-1)}
      >
        ← Retour au formulaire
      </button>

      <main style={styles.content}>
        <header style={styles.introduction}>
          <div style={styles.step}>Étape 4 sur 5</div>

          <h1 style={styles.title}>
            Pièces justificatives
          </h1>

          <p style={styles.subtitle}>
            Ajoutez les documents nécessaires à l’étude de votre demande.
            Les fichiers seront vérifiés avant leur envoi.
          </p>
        </header>

        <div style={styles.summary}>
          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Profil</span>

            <strong style={styles.summaryValue}>
              {profileLabel}
            </strong>
          </div>

          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>
              Financement
            </span>

            <strong style={styles.summaryValue}>
              {financingLabel}
            </strong>
          </div>

          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>
              Documents obligatoires
            </span>

            <strong style={styles.summaryValue}>
              {uploadedRequiredCount} / {requiredCount}
            </strong>
          </div>
        </div>

        <section style={styles.documentsCard}>
          <div style={styles.documentsHeading}>
            <div style={styles.headingIcon}>📎</div>

            <div>
              <h2 style={styles.documentsTitle}>
                Documents à fournir
              </h2>

              <p style={styles.documentsDescription}>
                Formats acceptés : PDF, JPG, JPEG et PNG. Taille maximale :
                5 Mo par fichier et 10 Mo au total.
              </p>
            </div>
          </div>

          <div style={styles.documentsGrid}>
            {requiredDocuments.map((document) => {
              const selectedFile =
                uploadedDocuments[document.id];

              const error = errors[document.id];

              return (
                <div
                  key={document.id}
                  style={{
                    ...styles.documentItem,
                    ...(selectedFile
                      ? styles.documentItemCompleted
                      : {}),
                  }}
                >
                  <div style={styles.documentTop}>
                    <div style={styles.documentIcon}>
                      {document.icon}
                    </div>

                    <div
                      style={{
                        ...styles.statusIndicator,
                        ...(selectedFile
                          ? styles.statusIndicatorCompleted
                          : {}),
                      }}
                    >
                      {selectedFile ? "✓" : ""}
                    </div>
                  </div>

                  <h3 style={styles.documentTitle}>
                    {document.title}
                  </h3>

                  <div style={styles.requirementRow}>
                    <span
                      style={
                        document.required
                          ? styles.requiredBadge
                          : styles.optionalBadge
                      }
                    >
                      {document.required
                        ? "Obligatoire"
                        : "Recommandé"}
                    </span>
                  </div>

                  <p style={styles.documentDescription}>
                    {document.description}
                  </p>

                  {selectedFile ? (
                    <div style={styles.selectedFile}>
                      <div style={styles.fileInformation}>
                        <span style={styles.fileIcon}>📄</span>

                        <div style={styles.fileText}>
                          <strong style={styles.fileName}>
                            {selectedFile.name}
                          </strong>

                          <span style={styles.fileSize}>
                            {formatFileSize(
                              selectedFile.size
                            )}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        style={styles.removeButton}
                        onClick={() =>
                          removeDocument(document.id)
                        }
                      >
                        Supprimer
                      </button>
                    </div>
                  ) : (
                    <label
                      style={styles.uploadButton}
                      htmlFor={`document-${document.id}`}
                    >
                      Choisir un fichier

                      <input
                        id={`document-${document.id}`}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                        style={styles.hiddenInput}
                        onChange={(event) =>
                          handleFileSelection(
                            document.id,
                            event
                          )
                        }
                      />
                    </label>
                  )}

                  {error && (
                    <p style={styles.errorMessage}>
                      {error}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <div style={styles.securityNotice}>
            <span style={styles.securityIcon}>🔒</span>

            <p style={styles.securityText}>
              Vos documents resteront confidentiels. Ils seront accessibles
              uniquement par vous et par les administrateurs autorisés à
              étudier votre dossier.
            </p>
          </div>

          <div style={styles.actions}>
            <button
              type="button"
              style={styles.previousButton}
              onClick={() => navigate(-1)}
            >
              ← Retour au formulaire
            </button>

            <button
              type="button"
              disabled={!allRequiredDocumentsUploaded}
              onClick={handleContinue}
              style={{
                ...styles.continueButton,
                ...(!allRequiredDocumentsUploaded
                  ? styles.disabledButton
                  : {}),
              }}
            >
              Continuer vers la vérification →
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: "40px 24px 90px",
    background:
      "linear-gradient(180deg, #070b18 0px, #0b1020 520px, #f8fafc 520px, #f8fafc 100%)",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  backButton: {
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#cbd5e1",
    fontSize: "13px",
    fontWeight: "800",
    cursor: "pointer",
  },

  content: {
    width: "100%",
    maxWidth: "1100px",
    margin: "70px auto 0",
  },

  introduction: {
    textAlign: "center",
  },

  step: {
    color: "#a78bfa",
    fontSize: "11px",
    fontWeight: "900",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },

  title: {
    margin: "16px 0 0",
    color: "#ffffff",
    fontSize: "clamp(34px, 5vw, 52px)",
    lineHeight: 1.1,
    letterSpacing: "-0.04em",
    fontWeight: "900",
  },

  subtitle: {
    margin: "18px auto 0",
    maxWidth: "650px",
    color: "#cbd5e1",
    fontSize: "15px",
    lineHeight: 1.8,
  },

  summary: {
    marginTop: "40px",
    padding: "20px",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    borderRadius: "20px",
    background: "rgba(255, 255, 255, 0.07)",
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
  },

  summaryItem: {
    padding: "18px",
    borderRadius: "14px",
    background: "rgba(2, 6, 23, 0.65)",
  },

  summaryLabel: {
    display: "block",
    color: "#94a3b8",
    fontSize: "11px",
    fontWeight: "700",
  },

  summaryValue: {
    display: "block",
    marginTop: "8px",
    color: "#ffffff",
    fontSize: "15px",
  },

  documentsCard: {
    marginTop: "30px",
    padding: "32px",
    border: "1px solid #e2e8f0",
    borderRadius: "24px",
    background: "#ffffff",
    boxShadow: "0 20px 55px rgba(15, 23, 42, 0.1)",
  },

  documentsHeading: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
  },

  headingIcon: {
    width: "46px",
    minWidth: "46px",
    height: "46px",
    borderRadius: "14px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
  },

  documentsTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "22px",
    fontWeight: "900",
  },

  documentsDescription: {
    margin: "8px 0 0",
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.6,
  },

  documentsGrid: {
    marginTop: "30px",
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "18px",
  },

  documentItem: {
    minHeight: "285px",
    padding: "22px",
    border: "2px solid #e2e8f0",
    borderRadius: "19px",
    background: "#ffffff",
    display: "flex",
    flexDirection: "column",
  },

  documentItemCompleted: {
    borderColor: "#22c55e",
    background: "#f0fdf4",
  },

  documentTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  documentIcon: {
    width: "50px",
    height: "50px",
    borderRadius: "15px",
    background: "#f5f3ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "23px",
  },

  statusIndicator: {
    width: "25px",
    height: "25px",
    border: "2px solid #cbd5e1",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "900",
  },

  statusIndicatorCompleted: {
    borderColor: "#22c55e",
    background: "#22c55e",
  },

  documentTitle: {
    margin: "18px 0 0",
    color: "#0f172a",
    fontSize: "17px",
    fontWeight: "900",
  },

  requirementRow: {
    marginTop: "10px",
  },

  requiredBadge: {
    display: "inline-flex",
    padding: "5px 8px",
    borderRadius: "999px",
    background: "#fef2f2",
    color: "#dc2626",
    fontSize: "10px",
    fontWeight: "900",
  },

  optionalBadge: {
    display: "inline-flex",
    padding: "5px 8px",
    borderRadius: "999px",
    background: "#f1f5f9",
    color: "#64748b",
    fontSize: "10px",
    fontWeight: "900",
  },

  documentDescription: {
    margin: "12px 0 18px",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.6,
  },

  uploadButton: {
    marginTop: "auto",
    padding: "12px 15px",
    border: "1px dashed #7c3aed",
    borderRadius: "11px",
    background: "#f5f3ff",
    color: "#7c3aed",
    textAlign: "center",
    fontSize: "12px",
    fontWeight: "900",
    cursor: "pointer",
  },

  hiddenInput: {
    display: "none",
  },

  selectedFile: {
    marginTop: "auto",
    padding: "12px",
    border: "1px solid #bbf7d0",
    borderRadius: "11px",
    background: "#ffffff",
  },

  fileInformation: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  fileIcon: {
    fontSize: "20px",
  },

  fileText: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },

  fileName: {
    maxWidth: "190px",
    overflow: "hidden",
    color: "#166534",
    fontSize: "11px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  fileSize: {
    marginTop: "3px",
    color: "#64748b",
    fontSize: "10px",
  },

  removeButton: {
    marginTop: "10px",
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#dc2626",
    fontSize: "11px",
    fontWeight: "800",
    cursor: "pointer",
  },

  errorMessage: {
    margin: "10px 0 0",
    color: "#dc2626",
    fontSize: "11px",
    lineHeight: 1.5,
  },

  securityNotice: {
    marginTop: "28px",
    padding: "15px 18px",
    borderRadius: "14px",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    gap: "11px",
  },

  securityIcon: {
    fontSize: "18px",
  },

  securityText: {
    margin: 0,
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.6,
  },

  actions: {
    marginTop: "28px",
    paddingTop: "24px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
  },

  previousButton: {
    padding: "14px 20px",
    border: "1px solid #cbd5e1",
    borderRadius: "13px",
    background: "#ffffff",
    color: "#334155",
    fontSize: "13px",
    fontWeight: "800",
    cursor: "pointer",
  },

  continueButton: {
    padding: "15px 22px",
    border: "none",
    borderRadius: "13px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "900",
    cursor: "pointer",
    boxShadow:
      "0 12px 26px rgba(37, 99, 235, 0.2)",
  },

  disabledButton: {
    background: "#cbd5e1",
    boxShadow: "none",
    cursor: "not-allowed",
  },
};

export default FinanceDocuments;